import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AppNav
 *
 * Top-of-screen tab bar for the app surface.
 *
 * Two kinds of tabs live here:
 *
 *   1. Fixed tabs — Home + Collections + Groups. Icon-only when inactive,
 *      icon + label when active. Neutral gray styling with NO borders in
 *      any state; hover, active, and popover-open just swap in a subtly
 *      darker background. The Groups tab is special: clicking it always
 *      opens the group-switcher popover (rather than navigating somewhere);
 *      its "active" state lights up when the current page is a group.
 *
 *   2. Collection tabs — dynamically opened per collection. Each is tinted
 *      from the same palette short text blocks use (via a hash of the
 *      collection id). Inactive tabs read as a plain neutral gray pill so
 *      the row stays calm; on hover or when active, the tab adopts its
 *      palette colors for bg, border, and text (hover and active use the
 *      SAME colors — per spec). A close (X) button appears on hover /
 *      active too. Middle-click anywhere on a collection tab closes it
 *      (same navigation rules as the X).
 *
 * Keyboard shortcuts: `q` → Home, `w` → Collections, `e` → Groups switcher,
 * `1-9` → Nth open collection tab. Modifier combos and typing in editable
 * elements are ignored so OS shortcuts and forms still work.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "@shared/client/components/toast/show-toast";
import { faArrowsRotate, faHouse, faPeoplePantsSimple, faRectangleHistory, faThumbtack, faXmark, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { KeyboardShortcut } from "@shared/client/components/ui/keyboard-shortcut/keyboard-shortcut";
import { Tooltip } from "@shared/client/components/ui/tooltip/tooltip";
import { getCollectionPaletteColor } from "@/config/block-styles";
import { useAppCollectionTabs, closeCollectionTab, reorderTab, } from "@/screens/app-screen/app-tabs-store";
import { collectionStore } from "@/data/collection-store";
import { useActiveCollectionId, useUrlForCollectionId } from "@/lib/urls";
import { track } from "@/lib/analytics";
import { MAX_PINS_PER_SURFACE } from "@shared/types/pin";
import { togglePinWithSync, usePinList } from "@/state/pin-store";
import { SHORTCUTS } from "@/keyboard/shortcuts";
import { useShortcut } from "@/keyboard/use-shortcut";
import { GroupSwitcher } from "./group-switcher";
import { AccountMenu } from "@/components/account-menu/account-menu";
import { FeedChatLauncher } from "@/components/feed-chat-launcher/feed-chat-launcher";
import { NotificationsPopup } from "@/components/notifications/notifications-popup";
import "./app-nav.scss";
const FIXED_TABS = [
    { id: "home", path: "/", icon: faHouse, label: "Home", shortcut: "Q" },
    {
        id: "collections",
        path: "/collections",
        icon: faRectangleHistory,
        label: "Collections",
        shortcut: "W",
    },
];
function collectionTabColors(collectionId) {
    const palette = getCollectionPaletteColor(collectionId);
    // Per the spec: hover and active share one set of colors. We pick the
    // pale `bg` + stronger `border` from the palette so the engaged tab
    // reads as a tinted pill, and the palette's `text` for legible ink.
    // The close-button hover bg also reuses `border` directly via CSS
    // (no extra channel needed).
    return {
        bg: palette.bg,
        border: palette.border,
        text: palette.text,
    };
}
function computeDropSide(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    return e.clientX < midX ? "before" : "after";
}
function computeFinalIndex(fromIndex, targetIndex, side) {
    const adjustedTarget = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
    return side === "after" ? adjustedTarget + 1 : adjustedTarget;
}
// A number-key shortcut should NOT fire when the user is typing — the
// collection-create input, rename flows, and any rich-text editor embedded
// in the stream would otherwise swallow keystrokes whole.
function isEditableTarget(target) {
    if (!(target instanceof HTMLElement))
        return false;
    if (target.isContentEditable)
        return true;
    const tag = target.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const AppNav = memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const allCollectionTabs = useAppCollectionTabs();
    const { groups } = collectionStore.useListGroups();
    // Pinned nav tabs are server-persisted (`account / tab` surface) and
    // sit between fixed tabs and the ephemeral tab strip. They're always
    // visible regardless of the open-tab list, so a collection that's
    // both pinned and open shows up once — as a pinned tab.
    const pinnedTabPins = usePinList({ type: "account" }, "tab");
    const pinnedCollectionIds = useMemo(() => new Set(pinnedTabPins.filter((p) => p.type === "collection").map((p) => p.id)), [pinnedTabPins]);
    const collectionTabs = useMemo(() => allCollectionTabs.filter((t) => !pinnedCollectionIds.has(t.collectionId)), [allCollectionTabs, pinnedCollectionIds]);
    const [draggingId, setDraggingId] = useState();
    const [dropIndicator, setDropIndicator] = useState();
    const [groupSwitcherOpenSignal, setGroupSwitcherOpenSignal] = useState(0);
    // Tab DOM nodes by id — used so keyboard shortcuts can move DOM focus
    // to the newly-activated tab. Without this, focus stays on whichever
    // tab the user last clicked, which (a) leaves a stale focus ring on
    // the old tab and (b) breaks subsequent Tab traversal because focus
    // resumes from the wrong place.
    const tabRefs = useRef(new Map());
    const setTabRef = useCallback((id, el) => {
        if (el)
            tabRefs.current.set(id, el);
        else
            tabRefs.current.delete(id);
    }, []);
    // Focus a tab without painting the `:focus-visible` ring. We mark the
    // node with `data-suppress-focus-ring` so the SCSS rule skips the
    // outline; the attribute is removed on the next blur so a *real*
    // keyboard focus (Tab traversal) still gets the ring as expected.
    const focusTabSilently = useCallback((id) => {
        const el = tabRefs.current.get(id);
        if (!el)
            return;
        el.dataset.suppressFocusRing = "true";
        const clear = () => {
            delete el.dataset.suppressFocusRing;
            el.removeEventListener("blur", clear);
        };
        el.addEventListener("blur", clear);
        el.focus({ preventScroll: true });
    }, []);
    const activeFixedTab = FIXED_TABS.find((t) => location.pathname === t.path)?.id;
    // Single source of truth — `useActiveCollectionId` resolves all four
    // collection-rendering URL shapes (`/g/:id`, `/c/:slug`, `/:name/:slug`,
    // legacy `/collection/:id`) back to the canonical entity ID via the live
    // cache. The matching `tab.collectionId === activeCollectionId` check
    // then naturally lights up the right tab regardless of which URL the
    // user landed on. Groups also live at `/g/:id`, so the same ID doubles
    // as the "active group" lookup key when the entity happens to be a group
    // rather than a collection (group-slug URLs `/:name` would still need a
    // separate slug→group resolver; not handled here).
    const activeCollectionId = useActiveCollectionId();
    const activeGroup = useMemo(() => activeCollectionId ? groups.find((g) => g.id === activeCollectionId) : undefined, [activeCollectionId, groups]);
    const handleCloseCollection = useCallback((e, collectionId) => {
        e.stopPropagation();
        // If we're closing the active tab, pick a neighbor to navigate to
        // BEFORE removing the tab — this matches standard tab UX (e.g. browsers,
        // editors): closing the active tab moves focus to its right neighbor,
        // or its left neighbor if it was the rightmost. When no other
        // collection tabs remain we fall back to the Stream — `/` is the
        // canonical "nothing else queued up" home, more useful than dumping
        // the user into the (possibly large) Collections index.
        if (activeCollectionId === collectionId) {
            const closingIndex = collectionTabs.findIndex((t) => t.collectionId === collectionId);
            const neighbor = closingIndex >= 0
                ? (collectionTabs[closingIndex + 1] ?? collectionTabs[closingIndex - 1])
                : undefined;
            if (neighbor) {
                navigate(buildUrlForCollectionId(neighbor.collectionId));
            }
            else {
                navigate("/");
            }
        }
        closeCollectionTab(collectionId);
    }, [activeCollectionId, collectionTabs, navigate]);
    const handleCollectionTabAuxClick = useCallback((e) => {
        if (e.button !== 1)
            return;
        const collectionId = e.currentTarget.dataset.collectionId;
        if (collectionId === undefined || collectionId === "")
            return;
        e.preventDefault();
        handleCloseCollection(e, collectionId);
    }, [handleCloseCollection]);
    const resetDrag = useCallback(() => {
        setDraggingId(undefined);
        setDropIndicator(undefined);
    }, []);
    const handleDragStart = useCallback((e, collectionId) => {
        setDraggingId(collectionId);
        e.dataTransfer.effectAllowed = "move";
        // Required for Firefox to start the drag session.
        e.dataTransfer.setData("text/plain", collectionId);
    }, []);
    const handleDragOver = useCallback((e, targetId) => {
        if (draggingId === undefined)
            return;
        // preventDefault is required for the browser to treat this as a valid
        // drop zone — without it, onDrop will never fire.
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (targetId === draggingId) {
            if (dropIndicator !== undefined)
                setDropIndicator(undefined);
            return;
        }
        const side = computeDropSide(e);
        if (dropIndicator === undefined ||
            dropIndicator.targetId !== targetId ||
            dropIndicator.side !== side) {
            setDropIndicator({ targetId, side });
        }
    }, [draggingId, dropIndicator]);
    const handleDrop = useCallback((e, targetId) => {
        e.preventDefault();
        if (draggingId === undefined || targetId === draggingId) {
            resetDrag();
            return;
        }
        const fromIndex = collectionTabs.findIndex((t) => t.collectionId === draggingId);
        const targetIndex = collectionTabs.findIndex((t) => t.collectionId === targetId);
        if (fromIndex < 0 || targetIndex < 0) {
            resetDrag();
            return;
        }
        const side = computeDropSide(e);
        const finalIndex = computeFinalIndex(fromIndex, targetIndex, side);
        reorderTab(fromIndex, finalIndex);
        resetDrag();
    }, [collectionTabs, draggingId, resetDrag]);
    useShortcut(SHORTCUTS.navHome, () => {
        navigate("/");
        focusTabSilently("home");
    });
    useShortcut(SHORTCUTS.navCollections, () => {
        navigate("/collections");
        focusTabSilently("collections");
    });
    // Intentionally doesn't navigate — the user may already be viewing a
    // group; `E` is a "let me pick a different group" affordance, not a
    // "show me the groups page" one.
    useShortcut(SHORTCUTS.openGroupSwitcher, () => {
        setGroupSwitcherOpenSignal((n) => n + 1);
    });
    // 1-9 stays inline: the bound action depends on the runtime collection
    // tabs list, which doesn't fit a static catalog entry. Same gating
    // rules as the catalog-driven shortcuts above (no modifiers, skip
    // editable targets) live in this handler.
    useEffect(() => {
        function onKeyDown(e) {
            if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey)
                return;
            if (isEditableTarget(e.target))
                return;
            const digit = Number(e.key);
            if (!Number.isInteger(digit) || digit < 1 || digit > 9)
                return;
            const tab = collectionTabs[digit - 1];
            if (!tab)
                return;
            e.preventDefault();
            track({ name: "collection.opened", props: { source: "nav" } });
            navigate(buildUrlForCollectionId(tab.collectionId));
            focusTabSilently(tab.collectionId);
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [collectionTabs, navigate, focusTabSilently, buildUrlForCollectionId]);
    return (_jsxs("nav", { className: "app-nav", children: [_jsxs("div", { className: "app-nav__leading", children: [FIXED_TABS.map((tab) => {
                        const isActive = tab.id === activeFixedTab;
                        const classes = [
                            "app-nav__tab",
                            "app-nav__tab--fixed",
                            isActive ? "app-nav__tab--active" : "",
                        ]
                            .filter(Boolean)
                            .join(" ");
                        return (_jsxs(React.Fragment, { children: [_jsx(Tooltip, { label: tab.label, kbd: tab.shortcut, side: "bottom", children: _jsxs("button", { ref: (el) => setTabRef(tab.id, el), type: "button", className: classes, onClick: () => navigate(tab.path), children: [_jsx(FaIcon, { icon: tab.icon, className: "app-nav__icon" }), _jsx("span", { className: "app-nav__label", children: tab.label }), _jsx(KeyboardShortcut, { keys: [tab.shortcut], size: "mini", className: "app-nav__kbd", "aria-label": `Shortcut: ${tab.shortcut}` })] }) }), tab.id === "home" ? (_jsx("div", { className: "app-nav__divider app-nav__divider--dynamic", "aria-hidden": "true" })) : null] }, tab.id));
                    }), _jsx(GroupSwitcher, { icon: faPeoplePantsSimple, activeGroup: activeGroup, openSignal: groupSwitcherOpenSignal }), pinnedTabPins.length > 0 || collectionTabs.length > 0 ? (_jsx("div", { className: "app-nav__divider app-nav__divider--dynamic app-nav__divider--tabs-leading" })) : null] }), pinnedTabPins.length > 0 || collectionTabs.length > 0 ? (_jsxs("div", { className: "app-nav__tab-strip", children: [pinnedTabPins.length > 0 ? (_jsx("div", { className: "app-nav__pinned-tabs", children: pinnedTabPins.map((pin) => (_jsx(PinnedNavTab, { collectionId: pin.id, activeCollectionId: activeCollectionId, onActivate: () => {
                                track({ name: "collection.opened", props: { source: "nav" } });
                                navigate(buildUrlForCollectionId(pin.id));
                            } }, pin.id))) })) : null, pinnedTabPins.length > 0 && collectionTabs.length > 0 ? (_jsx("div", { className: "app-nav__divider app-nav__divider--dynamic" })) : null, collectionTabs.length > 0 ? (_jsx("div", { className: "app-nav__collection-tabs", children: collectionTabs.map((tab, i) => {
                            const isActive = tab.collectionId === activeCollectionId;
                            const isDragging = draggingId === tab.collectionId;
                            const indicatorSide = dropIndicator !== undefined &&
                                dropIndicator.targetId === tab.collectionId
                                ? dropIndicator.side
                                : undefined;
                            const colors = collectionTabColors(tab.collectionId);
                            // 1-based position doubles as both the visible slot number and the
                            // keyboard shortcut target (1-9; tabs beyond 9 still show the number
                            // but have no shortcut).
                            const slotNumber = i + 1;
                            const style = {
                                "--tab-bg": colors.bg,
                                "--tab-border": colors.border,
                                "--tab-text": colors.text,
                            };
                            const classes = [
                                "app-nav__tab",
                                "app-nav__tab--collection",
                                isActive ? "app-nav__tab--active" : "",
                                isDragging ? "app-nav__tab--dragging" : "",
                                indicatorSide === "before" ? "app-nav__tab--drop-before" : "",
                                indicatorSide === "after" ? "app-nav__tab--drop-after" : "",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            const handleActivate = () => {
                                track({ name: "collection.opened", props: { source: "nav" } });
                                navigate(buildUrlForCollectionId(tab.collectionId));
                            };
                            return (_jsxs("div", { ref: (el) => setTabRef(tab.collectionId, el), role: "button", tabIndex: 0, className: classes, style: style, title: tab.title, "data-collection-id": tab.collectionId, draggable: true, onClick: handleActivate, onAuxClick: handleCollectionTabAuxClick, onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleActivate();
                                    }
                                }, onDragStart: (e) => handleDragStart(e, tab.collectionId), onDragEnter: (e) => {
                                    // preventDefault on dragEnter is needed by Safari for the
                                    // element to become a valid drop target.
                                    if (draggingId !== undefined)
                                        e.preventDefault();
                                }, onDragOver: (e) => handleDragOver(e, tab.collectionId), onDrop: (e) => handleDrop(e, tab.collectionId), onDragEnd: resetDrag, children: [_jsxs("span", { className: "app-nav__lead", "aria-hidden": "true", children: [_jsx("span", { className: "app-nav__num", children: slotNumber }), _jsx("span", { className: "app-nav__close", role: "button", tabIndex: -1, "aria-label": `Close ${tab.title}`, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => handleCloseCollection(e, tab.collectionId), onKeyDown: (e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        handleCloseCollection(e, tab.collectionId);
                                                    }
                                                }, children: _jsx(FaIcon, { icon: faXmark }) })] }), _jsx("span", { className: "app-nav__label", children: tab.title || "Untitled" }), _jsx("span", { className: "app-nav__pin-action", role: "button", tabIndex: -1, "aria-label": `Pin ${tab.title} as a nav tab`, title: "Pin as nav tab", onMouseDown: (e) => e.stopPropagation(), onClick: (e) => {
                                            e.stopPropagation();
                                            void togglePinWithSync({ type: "account" }, "tab", { type: "collection", id: tab.collectionId }).then((result) => {
                                                if (result.ok === false && result.reason === "max") {
                                                    showToast.warning(`You cannot pin more than ${MAX_PINS_PER_SURFACE} tabs`);
                                                }
                                            });
                                        }, children: _jsx(FaIcon, { icon: faThumbtack }) })] }, tab.collectionId));
                        }) })) : null] })) : null, _jsxs("div", { className: "app-nav__trailing", children: [_jsx(AccountMenu, {}), _jsx(FeedChatLauncher, {}), _jsx(NotificationsPopup, {}), _jsx("div", { className: "app-nav__divider app-nav__divider--mobile-trailing", "aria-hidden": "true" }), _jsx("button", { type: "button", className: "app-nav__refresh", "aria-label": "Refresh page", title: "Refresh", onClick: () => window.location.reload(), children: _jsx(FaIcon, { icon: faArrowsRotate, className: "app-nav__refresh-icon" }) })] })] }));
});
const PinnedNavTab = memo(({ collectionId, activeCollectionId, onActivate, }) => {
    const collection = collectionStore.useFindById(collectionId);
    const title = collection?.title?.trim() ? collection.title : "Untitled";
    const colors = collectionTabColors(collectionId);
    const isActive = activeCollectionId === collectionId;
    const style = {
        "--tab-bg": colors.bg,
        "--tab-border": colors.border,
        "--tab-text": colors.text,
    };
    const classes = [
        "app-nav__tab",
        "app-nav__tab--collection",
        "app-nav__tab--pinned",
        isActive ? "app-nav__tab--active" : "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { role: "button", tabIndex: 0, className: classes, style: style, title: title, "data-collection-id": collectionId, onClick: onActivate, onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onActivate();
            }
        }, children: [_jsx("span", { className: "app-nav__lead", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faThumbtack, className: "app-nav__pin-icon" }) }), _jsx("span", { className: "app-nav__label", children: title }), _jsx("span", { className: "app-nav__pin-action app-nav__pin-action--active", role: "button", tabIndex: -1, "aria-label": `Unpin ${title}`, title: "Unpin", onMouseDown: (e) => e.stopPropagation(), onClick: (e) => {
                    e.stopPropagation();
                    void togglePinWithSync({ type: "account" }, "tab", { type: "collection", id: collectionId });
                }, children: _jsx(FaIcon, { icon: faXmark }) })] }));
});
